import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { operatorApiRoutesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Home, ChevronRight, Save, Trash2, RefreshCw, ArrowRightLeft, Server,
} from 'lucide-react';

const OPERATORS = [
  { value: 'airtel', label: 'Airtel' },
  { value: 'jio', label: 'JIO' },
  { value: 'vi', label: 'VI' },
  { value: 'bsnl', label: 'BSNL' },
  { value: 'bsnl_special', label: 'BSNL Special' },
  { value: 'airtel_dth', label: 'Airtel DTH' },
  { value: 'tata_sky', label: 'Tata Sky' },
  { value: 'dish_tv', label: 'Dish TV' },
  { value: 'd2h', label: 'D2H' },
  { value: 'sun_direct', label: 'Sun Direct' },
  { value: 'electricity', label: 'Electricity' },
  { value: 'google_play', label: 'Google Play' },
];

const API_SLOTS = ['api_1', 'api_2', 'api_3', 'api_4', 'api_5'];

export default function ChangeApi() {
  const [routes, setRoutes] = useState([]);
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [selectedOp, setSelectedOp] = useState('');
  const [form, setForm] = useState({ api_1: '', api_2: '', api_3: '', api_4: '', api_5: '' });

  const fetchData = useCallback(async () => {
    try {
      const res = await operatorApiRoutesAPI.list();
      setRoutes(res.data.routes || []);
      setApis(res.data.apis || []);
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // When operator is selected, load existing route
  useEffect(() => {
    if (selectedOp) {
      const existing = routes.find(r => r.operator === selectedOp);
      if (existing) {
        setForm({
          api_1: existing.api_1 || '',
          api_2: existing.api_2 || '',
          api_3: existing.api_3 || '',
          api_4: existing.api_4 || '',
          api_5: existing.api_5 || '',
        });
      } else {
        setForm({ api_1: '', api_2: '', api_3: '', api_4: '', api_5: '' });
      }
    }
  }, [selectedOp, routes]);

  const handleSave = async () => {
    if (!selectedOp) { toast.error('Select an operator'); return; }
    setSaving(selectedOp);
    try {
      await operatorApiRoutesAPI.save({ operator: selectedOp, ...form });
      toast.success(`${selectedOp.toUpperCase()} API route saved`);
      fetchData();
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (id, opName) => {
    if (!window.confirm(`"${opName}" route delete করবেন?`)) return;
    try {
      await operatorApiRoutesAPI.delete(id);
      toast.success('Route deleted');
      setSelectedOp('');
      setForm({ api_1: '', api_2: '', api_3: '', api_4: '', api_5: '' });
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const getApiName = (id) => {
    const a = apis.find(a => a.id === id);
    return a ? a.name : '-';
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="change-api-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Home className="w-4 h-4" />
          <ChevronRight className="w-3 h-3" />
          <span className="text-slate-400">Settings</span>
          <ChevronRight className="w-3 h-3" />
          <span className="font-medium text-slate-700">Change API</span>
        </div>

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Change API</h1>
          <p className="text-sm text-slate-500 mt-1">Operator wise API routing - set priority order per operator</p>
        </div>

        {loading ? (
          <Skeleton className="h-64 w-full rounded-xl" />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Left: Form */}
            <Card className="border shadow-sm rounded-xl" data-testid="change-api-form">
              <CardHeader className="pb-3 pt-5 px-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                    <ArrowRightLeft className="w-5 h-5 text-[#4680ff]" />
                  </div>
                  <CardTitle className="text-base font-bold">Set API Priority</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-5 space-y-4">
                {/* API Slots */}
                {API_SLOTS.map((slot, i) => (
                  <div key={slot}>
                    <label className="text-xs font-bold text-[#4680ff] uppercase tracking-wider mb-1 block">API {i + 1}</label>
                    <select
                      value={form[slot]}
                      onChange={e => setForm(prev => ({ ...prev, [slot]: e.target.value }))}
                      className="w-full h-10 text-sm border border-slate-200 rounded-lg px-3 bg-white focus:ring-2 focus:ring-[#4680ff] focus:border-[#4680ff] outline-none"
                      data-testid={`select-${slot}`}
                    >
                      <option value="">- Select -</option>
                      {apis.map(api => (
                        <option key={api.id} value={api.id} disabled={api.status !== 'active'}>
                          {api.name} {api.status !== 'active' ? '(Inactive)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}

                {/* Operator Select */}
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Operator</label>
                  <select
                    value={selectedOp}
                    onChange={e => setSelectedOp(e.target.value)}
                    className="w-full h-10 text-sm border border-slate-200 rounded-lg px-3 bg-white focus:ring-2 focus:ring-[#4680ff] focus:border-[#4680ff] outline-none"
                    data-testid="select-operator"
                  >
                    <option value="">- Select -</option>
                    {OPERATORS.map(op => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                </div>

                {/* Save Button */}
                <Button
                  onClick={handleSave}
                  disabled={!selectedOp || saving}
                  className="w-full h-10 bg-[#4680ff] hover:bg-[#2563eb] text-white font-semibold"
                  data-testid="save-route-btn"
                >
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {saving ? 'Saving...' : 'Change API'}
                </Button>
              </CardContent>
            </Card>

            {/* Right: Existing Routes */}
            <Card className="border shadow-sm rounded-xl" data-testid="existing-routes">
              <CardHeader className="pb-3 pt-5 px-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <Server className="w-5 h-5 text-emerald-600" />
                  </div>
                  <CardTitle className="text-base font-bold">Configured Routes ({routes.length})</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-5">
                {routes.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">No operator routes configured yet. Uses global priority.</p>
                ) : (
                  <div className="space-y-3">
                    {routes.map(route => (
                      <div
                        key={route.id}
                        className={`p-3 rounded-lg border transition-all cursor-pointer ${selectedOp === route.operator ? 'border-[#4680ff] bg-blue-50' : 'border-slate-100 bg-slate-50 hover:border-slate-200'}`}
                        onClick={() => setSelectedOp(route.operator)}
                        data-testid={`route-${route.operator}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Badge className="bg-slate-800 text-white text-[10px] uppercase">{route.operator}</Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                            onClick={e => { e.stopPropagation(); handleDelete(route.id, route.operator); }}
                            data-testid={`delete-route-${route.operator}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-5 gap-1">
                          {API_SLOTS.map((slot, i) => (
                            <div key={slot} className="text-center">
                              <span className="text-[9px] font-bold text-[#4680ff] block">API {i + 1}</span>
                              <span className="text-[10px] text-slate-600 font-medium truncate block" title={route[slot] ? getApiName(route[slot]) : '-'}>
                                {route[slot] ? getApiName(route[slot]).split(' ')[0] : '-'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
